import { useRouter } from 'expo-router'
import { useAuth } from '@/lib/auth'
import { captureException, errorToString } from '@/lib/errors'
import { performMutations, useMutation } from '@/lib/mutations'
import { notify } from '@/lib/notice'
import { pb, useStore } from '@/lib/pocketbase'
import {
    buildUserOrgUpdateData,
    buildUserUpdateData,
    type ChangePasswordFormData,
    computeChanges,
    getSuccessMessage,
    type MyDetailsFormData,
    type UserOrgWorkingHoursData,
} from '@/stores/my-details-store'
import { useNavigateBack } from './useNavigateBack'

interface UpdateSettingsParams {
    data: MyDetailsFormData
    orgSlug: string
    userOrgId: string | null
    userOrgData?: UserOrgWorkingHoursData
}

export function useUpdateSettings() {
    const { user } = useAuth()
    const [usersCollection, userOrgCollection] = useStore('users', 'user_org')
    const { navigateBackTo } = useNavigateBack()

    const mutation = useMutation({
        mutationFn: async ({ data, orgSlug, userOrgId, userOrgData }: UpdateSettingsParams) => {
            const changes = computeChanges(data, user, userOrgData)

            await performMutations(function* () {
                if (changes.hasProfileChanges || changes.hasGeoTrackingChanges) {
                    const userUpdateData = buildUserUpdateData(data, user, changes)
                    yield usersCollection.update(user.id, draft => {
                        Object.assign(draft, userUpdateData)
                    })
                }

                if (
                    (changes.hasWorkingHoursChanges || changes.hasTrackingPauseChanges) &&
                    userOrgId
                ) {
                    const userOrgUpdateData = buildUserOrgUpdateData(data, changes)
                    yield userOrgCollection.update(userOrgId, draft => {
                        Object.assign(draft, userOrgUpdateData)
                    })
                }
            })

            return { changes, orgSlug }
        },
        onSuccess: ({ changes, orgSlug }) => {
            const message = getSuccessMessage(changes)
            if (message) {
                notify.success(message)
            }
            navigateBackTo(`/${orgSlug}/settings/`)
        },
        onError: error => {
            notify.error(errorToString(error))
        },
    })

    return {
        mutate: mutation.mutate,
        mutateAsync: mutation.mutateAsync,
        isPending: mutation.isPending,
    }
}

export function useChangePassword() {
    const { user } = useAuth()

    const mutation = useMutation({
        mutationFn: async (data: ChangePasswordFormData) => {
            await pb.collection('users').update(user.id, {
                oldPassword: data.oldPassword,
                password: data.password,
                passwordConfirm: data.passwordConfirm,
            })
        },
        onSuccess: () => {
            notify.success('Password changed successfully')
        },
        onError: error => {
            notify.error(errorToString(error))
        },
    })

    return {
        mutate: mutation.mutate,
        mutateAsync: mutation.mutateAsync,
        isPending: mutation.isPending,
    }
}

export function useDeleteAccount() {
    const { logout } = useAuth()
    const router = useRouter()
    const [userOrgCollection] = useStore('user_org')

    const mutation = useMutation({
        mutationFn: async ({ userOrgId }: { userOrgId: string }) => {
            await performMutations(function* () {
                yield userOrgCollection.update(userOrgId, draft => {
                    const extendedDraft = draft as typeof draft & { inactive_at?: string }
                    extendedDraft.inactive_at = new Date().toISOString()
                })
            })
        },
        onSuccess: () => {
            logout()
            router.replace('/auth/sign-in')
            notify.success('Account deleted successfully')
        },
        onError: error => {
            captureException('Delete account', error)
            notify.error(errorToString(error))
        },
    })

    return {
        mutate: mutation.mutate,
        mutateAsync: mutation.mutateAsync,
        isPending: mutation.isPending,
    }
}
